import { useSignIn, useSignUp } from "@clerk/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { ErrorHandler } from "@/web/lib/errors";

export const Route = createFileRoute("/callback/auth")({
  component: AuthCallbackPage,
});

function getErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "errors" in error) {
    const clerkError = error as { errors?: Array<{ longMessage?: string; message?: string }> };
    const firstError = clerkError.errors?.[0];
    if (firstError?.longMessage) return firstError.longMessage;
    if (firstError?.message) return firstError.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please request a new magic link.";
}

function getRedirectTarget(search: string): string {
  const params = new URLSearchParams(search);
  const redirectUrl = params.get("redirect_url");

  if (!redirectUrl?.startsWith("/")) {
    return "/";
  }

  // `startsWith("/")` alone is not enough: `//evil.example` and the backslash
  // variant `/\evil.example` also start with `/` but resolve to a different
  // origin, which `window.location.assign` would follow — an open redirect.
  // Resolve against our own origin and only accept targets that stay on it,
  // returning path+query+hash so we never hand `assign` an absolute URL.
  try {
    const resolved = new URL(redirectUrl, window.location.origin);
    if (resolved.origin !== window.location.origin) {
      return "/";
    }
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch (error) {
    // Malformed `redirect_url` is untrusted user input, not an app fault — log
    // for debugging without paging Sentry, and fall back to the safe default.
    ErrorHandler.logError(error);
    return "/";
  }
}

function AuthCallbackPage() {
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const navigate = useNavigate();
  const redirectTarget = useMemo(() => getRedirectTarget(window.location.search), []);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isFinalizingRef = useRef(false);

  useEffect(() => {
    if (!signIn || !signUp || isFinalizingRef.current) {
      return;
    }

    const signInLinkStatus = signIn.emailLink.verification?.status;
    const signUpLinkStatus = signUp.verifications.emailLinkVerification?.status;

    if (signInLinkStatus === "expired" || signUpLinkStatus === "expired") {
      setErrorMessage("Your magic link expired. Please request a new one.");
      return;
    }

    // Only one side's email-link factor verifies for a given link; that tells us
    // which flow this callback belongs to.
    const resource =
      signInLinkStatus === "verified" ? signIn : signUpLinkStatus === "verified" ? signUp : null;
    if (!resource) {
      return;
    }

    // A verified email-link factor is not a finished flow: a sign-in can still
    // need a second factor and a sign-up can still be `missing_requirements`. In
    // both cases no session has been created, so `finalize()` throws "Cannot
    // finalize sign-in without a created session" (TYCHO-1H). Gate on the overall
    // status like the email-code path does, and surface a clear message for the
    // incomplete case instead of crashing.
    if (resource.status !== "complete") {
      setErrorMessage(
        "We couldn't finish signing you in. Please request a new magic link or try another method.",
      );
      return;
    }

    isFinalizingRef.current = true;
    void resource
      .finalize()
      .then(({ error }) => {
        if (error) {
          isFinalizingRef.current = false;
          setErrorMessage(getErrorMessage(error));
          return;
        }

        // Hard reload rather than SPA navigation so the SpacetimeDB connection
        // boots with the freshly minted auth token — same reason the email-code
        // finalize path in `useAuthnDialogFlow` uses `window.location.assign`.
        window.location.assign(redirectTarget);
      })
      .catch((error: unknown) => {
        ErrorHandler.reportError(error);
        isFinalizingRef.current = false;
        setErrorMessage(getErrorMessage(error));
      });
  }, [redirectTarget, signIn, signUp]);

  if (!errorMessage) {
    return <FullPageLoader />;
  }

  return (
    <div className="h-full-viewport flex items-center justify-center px-6">
      <div className="flex w-full max-w-120 flex-col items-center gap-4 text-center">
        <h1 className="font-heading text-2xl uppercase">We couldn&apos;t complete sign in</h1>
        <p className="text-sm text-primary/80">{errorMessage}</p>
        <Button onClick={() => void navigate({ to: redirectTarget })}>Back</Button>
      </div>
    </div>
  );
}
